import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Alert,
  Badge,
  ButtonGroup,
  Button,
  Spinner,
} from '../shim/reactstrap';
import { createChart } from 'lightweight-charts';

// Range buttons shown in the chart's top-right selector. The values
// (`1D`, `1M`, `1Y`, `All`) are the same tokens the controller's
// `ResolveRange` switch understands — see
// `TradingStrategyController.GetPortfolioHistory`.
const RANGE_OPTIONS = [
  { value: '1D',  label: '1D',  days: 1 },
  { value: '1M',  label: '1M',  days: 30 },
  { value: '1Y',  label: '1Y',  days: 365 },
  { value: 'All', label: 'All', days: null },
];

// Build the lightweight-charts time-bucket string the chart axis uses to
// decide where to draw tick labels. Sub-day ranges want HH:MM, monthly+
// ranges only need YYYY-MM-DD.
function pickTimeFormat(range) {
  if (range === '1D') return { timeVisible: true, secondsVisible: false };
  return { timeVisible: false, secondsVisible: false };
}

// Format a USD value with thousands separators + the broker-reported
// currency code (defaults to USD when the broker omitted one).
function formatCurrency(value, currency) {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  const opts = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return `${num.toLocaleString(undefined, opts)} ${currency || 'USD'}`;
}

// Format a fractional PnL percentage (e.g. 0.05 → "+5.00%"). Colour the
// output based on sign so the user can see at a glance whether the
// window is green or red.
function formatPnlPercent(value) {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  const sign = num > 0 ? '+' : '';
  const className = num >= 0 ? 'text-success' : 'text-danger';
  return <span className={className}>{sign}{(num * 100).toFixed(2)}%</span>;
}

/**
 * Portfolio equity + PnL line chart for the trading-strategy view page.
 * Renders a single lightweight-charts instance with two line series:
 *   • Equity   (cash + positions, USD)        — blue
 *   • Profit/Loss vs broker base value (USD) — green / red
 *
 * The top-right range selector (1D / 1M / 1Y / All) maps onto the
 * `range` query string sent to the backend and defaults to 1M.
 */
export const PortfolioHistoryChart = ({ strategyId }) => {
  const { apiClient } = useAuth();
  const [range, setRange] = useState('1M');
  const [data, setData] = useState(null); // { Points, BaseValue, Currency, TimeFrame }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const equitySeriesRef = useRef(null);
  const pnlSeriesRef = useRef(null);
  const dataRef = useRef(null); // mirror of `data` for use in chart callbacks

  // Keep the ref in sync so the chart-update effect can read the latest
  // data without re-running (avoids recreating the chart on every load).
  useEffect(() => { dataRef.current = data; }, [data]);

  // Fetch the history whenever the strategy id or range changes. The
  // controller already does its own auth + connector validation, so we
  // just relay any error message back to the user.
  const loadHistory = useCallback(async (sid, r) => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.getTradingStrategyPortfolioHistory(sid, r);
      setData(resp || null);
    } catch (err) {
      setError(err?.message || 'Failed to load portfolio history.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (strategyId) loadHistory(strategyId, range);
  }, [strategyId, range, loadHistory]);

  // Build / tear down the lightweight-charts instance once per mount.
  // Subsequent data refreshes just call `setData()` on the existing
  // line series — no chart rebuild, no flicker.
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 320,
      layout: { background: { color: '#ffffff' }, textColor: '#333' },
      grid: { vertLines: { color: '#eee' }, horzLines: { color: '#eee' } },
      timeScale: { borderColor: '#ccc', timeVisible: false, secondsVisible: false },
      rightPriceScale: { borderColor: '#ccc' },
    });
    chartRef.current = chart;

    const equitySeries = chart.addLineSeries({
      color: '#0d6efd',
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
      title: 'Equity',
    });
    equitySeriesRef.current = equitySeries;

    // PnL shares the same price scale as Equity (both are USD values,
    // and the broker reports them on the same time axis) but lives as
    // its own series so the legend shows both lines.
    const pnlSeries = chart.addLineSeries({
      color: '#198754',
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
      title: 'P/L',
    });
    pnlSeriesRef.current = pnlSeries;

    const handleResize = () => {
      if (chartRef.current && container) {
        chartRef.current.applyOptions({ width: container.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
      }
      chartRef.current = null;
      equitySeriesRef.current = null;
      pnlSeriesRef.current = null;
    };
  }, []);

  // Push new data into the existing line series whenever the fetch
  // completes. Lightweight-charts requires ascending time values and
  // no duplicates on the same timestamp, so we sort + dedupe defensively.
  useEffect(() => {
    if (!data || !equitySeriesRef.current || !pnlSeriesRef.current) return;

    const points = Array.isArray(data.Points) ? data.Points : [];
    if (points.length === 0) {
      equitySeriesRef.current.setData([]);
      pnlSeriesRef.current.setData([]);
      return;
    }

    const equityData = [];
    const pnlData = [];
    let lastTs = -Infinity;
    for (const p of points) {
      // Alpaca returns UTC timestamps; lightweight-charts wants unix
      // seconds (UTC, business-day or timestamp modes). Timestamp is the
      // safe default — it works for both sub-day and multi-year ranges.
      const ts = Math.floor(new Date(p.TimestampUtc).getTime() / 1000);
      if (Number.isNaN(ts) || ts <= lastTs) continue;
      lastTs = ts;
      equityData.push({
        time: ts,
        value: Number(p.Equity) || 0,
      });
      pnlData.push({
        time: ts,
        value: Number(p.ProfitLoss) || 0,
      });
    }

    try {
      equitySeriesRef.current.setData(equityData);
      pnlSeriesRef.current.setData(pnlData);
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
        // For the 1D view the user expects HH:MM tick labels instead of
        // day-only ones; lightweight-charts decides this from the time
        // scale's `timeVisible` flag which we set per range below.
        const tf = pickTimeFormat(range);
        chartRef.current.timeScale().applyOptions(tf);
      }
    } catch (err) {
      // setData throws on out-of-order / duplicate timestamps; the
      // dedupe above should make that impossible, but a defensive catch
      // keeps a bad payload from killing the chart for the rest of the
      // session.
      console.error('PortfolioHistoryChart setData failed', err);
    }
  }, [data, range]);

  const points = data?.Points || [];
  const hasPoints = points.length > 0;
  const lastEquity = hasPoints ? Number(points[points.length - 1].Equity) : null;
  const lastPnl = hasPoints ? Number(points[points.length - 1].ProfitLoss) : null;
  const lastPnlPct = hasPoints ? Number(points[points.length - 1].ProfitLossPercentage) : null;
  const minEquity = hasPoints
    ? points.reduce((m, p) => Math.min(m, Number(p.Equity)), Number.POSITIVE_INFINITY)
    : null;
  const maxEquity = hasPoints
    ? points.reduce((m, p) => Math.max(m, Number(p.Equity)), Number.NEGATIVE_INFINITY)
    : null;

  return (
    <div className="mt-3">
      {/* Chart header: title on the left, range selector on the right.
          Mirrors the Alpaca dashboard layout the user is used to. */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <h3 className="h6 mb-0">Portfolio History</h3>
          {data?.TimeFrame && (
            <Badge color="secondary" pill>{data.TimeFrame}</Badge>
          )}
        </div>
        <ButtonGroup size="sm" aria-label="Portfolio history range">
          {RANGE_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              color={range === opt.value ? 'primary' : 'secondary'}
              outline={range !== opt.value}
              onClick={() => setRange(opt.value)}
              disabled={loading}
              aria-pressed={range === opt.value}
            >
              {opt.label}
            </Button>
          ))}
        </ButtonGroup>
      </div>

      {error && <Alert color="danger">{error}</Alert>}

      {loading && !data && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      <div
        ref={chartContainerRef}
        style={{ width: '100%', minHeight: '320px' }}
        aria-label="Portfolio equity and PnL chart"
      />

      {!loading && hasPoints && (
        <div
          className="d-flex flex-wrap mt-2 text-muted small"
          style={{ gap: '24px' }}
        >
          <div>
            <span className="d-block">Latest equity</span>
            <strong className="text-body">
              {formatCurrency(lastEquity, data?.Currency)}
            </strong>
          </div>
          <div>
            <span className="d-block">Window change</span>
            <strong className={lastPnl >= 0 ? 'text-success' : 'text-danger'}>
              {formatCurrency(lastPnl, data?.Currency)}
              {' '}
              {formatPnlPercent(lastPnlPct)}
            </strong>
          </div>
          <div>
            <span className="d-block">Window min</span>
            <strong className="text-body">{formatCurrency(minEquity, data?.Currency)}</strong>
          </div>
          <div>
            <span className="d-block">Window max</span>
            <strong className="text-body">{formatCurrency(maxEquity, data?.Currency)}</strong>
          </div>
          <div>
            <span className="d-block">Base value</span>
            <strong className="text-body">{formatCurrency(data?.BaseValue, data?.Currency)}</strong>
          </div>
          <div>
            <span className="d-block">Points</span>
            <strong className="text-body">{points.length}</strong>
          </div>
        </div>
      )}

      {!loading && !hasPoints && !error && (
        <p className="text-muted small mt-2 mb-0">
          No portfolio history available for this connector / range. The
          broker may not expose historical equity snapshots, or the account
          has no activity in this window.
        </p>
      )}
    </div>
  );
};
